import { useState } from 'react';
import { ArrowLeft, ArrowRight, Users, Repeat, CalendarCheck, Sparkles } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { useGatheringCategories } from '@/hooks/useGatherings';
import { getBrandHexByIndex } from '@/lib/categoryStyles';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import GatheringCategoryView from '@/components/gatherings/GatheringCategoryView';

import imgDiwaniya from '@/assets/gathering-diwaniya.png.asset.json';
import imgOpenDay from '@/assets/gathering-open-day.png.asset.json';
import imgSaturday from '@/assets/gathering-saturday.png.asset.json';
import imgFriday from '@/assets/gathering-friday.png.asset.json';
import imgGirls from '@/assets/gathering-girls.png.asset.json';

export const FALLBACK_IMAGES: Record<string, string> = {
  diwaniya: imgDiwaniya.url,
  'open-day': imgOpenDay.url,
  saturday: imgSaturday.url,
  'saturday-visit': imgSaturday.url,
  friday: imgFriday.url,
  'friday-visit': imgFriday.url,
  girls: imgGirls.url,
  'girls-gathering': imgGirls.url,
};


// Brand wheel color index per slug (from logo palette)
const SLUG_COLOR_INDEX: Record<string, number> = {
  diwaniya: 6,   // orange
  'open-day': 1, // sky
  saturday: 2,   // green
  friday: 10,    // violet
  girls: 8,      // pink
};

function pickFallback(slug: string, i: number) {
  if (FALLBACK_IMAGES[slug]) return FALLBACK_IMAGES[slug];
  const list = Object.values(FALLBACK_IMAGES);
  return list[i % list.length];
}

function pickColor(slug: string, i: number) {
  const idx = SLUG_COLOR_INDEX[slug] ?? i;
  return getBrandHexByIndex(idx);
}

export default function GatheringsSection() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const { data: categories = [], isLoading } = useGatheringCategories(true);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  if (!isLoading && categories.length === 0) return null;
  const Arrow = isArabic ? ArrowLeft : ArrowRight;


  const benefits = isArabic
    ? [
        { icon: Users, text: 'مدرب مختلف في كل جلسة' },
        { icon: Repeat, text: 'تجديد تلقائي بدون عناء' },
        { icon: CalendarCheck, text: 'جدولة مرنة تناسب وقتك' },
        { icon: Sparkles, text: 'تجربة حصرية بسعر مخفّض' },
      ]
    : [
        { icon: Users, text: 'A different host every session' },
        { icon: Repeat, text: 'Effortless auto-renewal' },
        { icon: CalendarCheck, text: 'Flexible scheduling' },
        { icon: Sparkles, text: 'Exclusive members-only pricing' },
      ];

  return (
    <>
    <section className="py-6 md:py-8">

      <div className="container mx-auto px-4">
        <div className="text-center mb-5 md:mb-7">
          <h2 className="text-2xl md:text-4xl font-extrabold bg-gradient-to-r from-[#2E68B8] via-[#E94B7B] to-[#F39A2B] bg-clip-text text-transparent leading-tight">
            {isArabic ? 'اشتراك اليمعات' : 'Gatherings Subscription'}
          </h2>
          <p className="text-xs md:text-base text-muted-foreground mt-2 font-medium">
            {isArabic ? 'اشترك مرة واحدة… واستمتع بمدرب مختلف كل مرة' : 'Subscribe once — enjoy a different host every time'}
          </p>

          <ul className="mt-5 md:mt-6 flex flex-col gap-3 md:gap-3.5 w-fit mx-auto ps-3 md:ps-6 text-start">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <li
                  key={i}
                  className="inline-flex items-center gap-3 text-sm md:text-base font-semibold text-foreground"
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5 shrink-0" color="#E94B7B" />
                  <span>{b.text}</span>
                </li>
              );
            })}
          </ul>

        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5 md:max-w-4xl md:mx-auto justify-items-center">
          {(isLoading ? Array.from({ length: 5 }) : categories).map((cat: any, i) => {
            if (!cat) {
              return <div key={i} className="w-full aspect-[4/5] rounded-2xl bg-muted animate-pulse" />;
            }
            const name = isArabic ? cat.name_ar : cat.name_en || cat.name_ar;
            const img = cat.cover_image_url || pickFallback(cat.slug, i);
            const color = pickColor(cat.slug, i);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setOpenSlug(cat.slug)}
                className="animate-fade-in group block w-full text-start"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div
                  className="aspect-[4/5] overflow-hidden relative rounded-2xl bg-muted shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1"
                  style={{ border: `6px solid ${color}` }}
                >
                  <img
                    src={img}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 start-3 end-3 text-white">
                    <h3 className="text-base md:text-xl font-extrabold drop-shadow-md leading-tight">
                      {name}
                    </h3>
                    <div
                      className="mt-2 inline-flex items-center gap-1 text-xs md:text-sm font-bold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: color, color: '#fff' }}
                    >
                      {isArabic ? 'اشتراك' : 'Subscribe'}
                      <Arrow className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </button>
            );

          })}
        </div>
      </div>
    </section>

    <Dialog open={!!openSlug} onOpenChange={(o) => !o && setOpenSlug(null)}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        {openSlug && (
          <GatheringCategoryView
            slug={openSlug}
            embedded
            onClose={() => setOpenSlug(null)}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

